import { Group, Text, useMantineTheme } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconCancel, IconChevronDown } from "@tabler/icons-react"
import { CertificateRequestStatus } from "@tfg/db"
import { Menu } from "@tfg/ui"
import { useTranslation } from "react-i18next"

import { certificateByOrganizationCertificateRequestDtoStatusEnum } from "~/gen/api"
import { useUpdateCertificateRequestsMutation } from "~/hooks/useUpdateCertificateRequestsMutation"
import { CertificateRequestStatusBadge } from "~/pages/Brand/Certificates/components/CertificateRequestStatusBadge"
import { RejectionModal } from "~/pages/Brand/Certificates/components/RejectionModal"

type Props = {
  certificateRequestStatus: CertificateRequestStatus
  certificateRequestId: string
  certificateId: string | null
  userType: "brand" | "supplier"
  onActionSelected?: () => void
}

export const CertificateRequestStatusBadgeWithActions = ({
  certificateRequestStatus,
  certificateRequestId,
  certificateId,
  userType,
  onActionSelected,
}: Props) => {
  const { t } = useTranslation("certificates")
  const theme = useMantineTheme()
  const [opened, { open, close }] = useDisclosure()

  const {
    mutate: updateCertificateRequest,
    isPending: isUpdatingCertificateRequest,
  } = useUpdateCertificateRequestsMutation({
    certificateId: certificateId ?? "",
    onSettled: () => {
      onActionSelected?.()
    },
  })

  const canReject =
    certificateRequestStatus === CertificateRequestStatus.ACCEPTED &&
    certificateId !== null

  if (!canReject) {
    return (
      <CertificateRequestStatusBadge
        certificateRequestStatus={certificateRequestStatus}
        userType={userType}
      />
    )
  }

  return (
    <>
      <Menu
        width="fit-content"
        styles={{
          dropdown: {
            maxWidth: 200,
          },
        }}
        position="bottom-start"
        withinPortal
      >
        <Menu.Target>
          <div
            style={{
              cursor: "pointer",
              display: "inline-block",
            }}
          >
            <CertificateRequestStatusBadge
              certificateRequestStatus={certificateRequestStatus}
              userType={userType}
              rightSection={
                <IconChevronDown
                  size={theme.other.icon.size}
                  stroke={theme.other.icon.stroke}
                />
              }
            />
          </div>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item onClick={open}>
            <Group gap="xs">
              <IconCancel
                color="var(--mantine-color-error)"
                size={theme.other.icon.size}
                stroke={theme.other.icon.stroke}
              />
              <Text>{t("certificateRequestActions.changeToRejected")}</Text>
            </Group>
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <RejectionModal
        onSubmit={(values) => {
          updateCertificateRequest(
            {
              data: {
                ids: [certificateRequestId],
                update: {
                  status:
                    certificateByOrganizationCertificateRequestDtoStatusEnum.REJECTED,
                  rejectionMessage: values.rejectionMessage,
                },
              },
            },
            {
              onSuccess: () => {
                close()
              },
            }
          )
        }}
        opened={opened}
        onClose={close}
        isSubmitting={isUpdatingCertificateRequest}
      />
    </>
  )
}
